import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { buildApiUrl } from '../services/apiHelpers';
import {
  Stethoscope,
  SlidersHorizontal,
  Monitor,
  ChevronLeft,
  ChevronRight,
  UserRound,
  X,
  Loader2,
  Radio,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Design tokens                                                      */
/*  Primary  – Indigo   : brand / navigation actions                   */
/*  Success  – Emerald   : "Open OPD" (a live, active queue)            */
/*  Violet             : "Queue Display" (broadcast / external screen)  */
/*  Indigo (primary)    : "Manage OPD" (configuration)                  */
/* ------------------------------------------------------------------ */

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@600;700;800&display=swap');

  .ms-wrap, .ms-portal-root {
    --ms-ink: #0b1220;
    --ms-muted: #5b7083;
    --ms-border: #e1e8f0;
    --ms-surface: #ffffff;
    --ms-surface-alt: #f7fafc;

    --ms-primary: #1d4ed8;
    --ms-primary-dark: #1a3fae;
    --ms-primary-tint: rgba(29, 78, 216, 0.09);
    --ms-primary-tint-strong: rgba(29, 78, 216, 0.16);

    --ms-success: #059669;
    --ms-success-tint: rgba(5, 150, 105, 0.1);
    --ms-success-tint-strong: rgba(5, 150, 105, 0.18);

    --ms-violet: #7c3aed;
    --ms-violet-tint: rgba(124, 58, 237, 0.1);
    --ms-violet-tint-strong: rgba(124, 58, 237, 0.18);

    --ms-radius-lg: 22px;
    --ms-radius-md: 16px;
    --ms-radius-sm: 12px;
    --ms-shadow-card: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 20px rgba(15, 23, 42, 0.06);
    --ms-shadow-card-hover: 0 10px 24px rgba(15, 23, 42, 0.10), 0 2px 6px rgba(15, 23, 42, 0.06);
    --ms-shadow-modal: 0 24px 64px rgba(15, 23, 42, 0.22), 0 4px 16px rgba(15, 23, 42, 0.08);
  }

  .ms-wrap *, .ms-portal-root * {
    box-sizing: border-box;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }

  @keyframes ms-overlay-in   { from { opacity: 0; } to { opacity: 1; } }
  @keyframes ms-modal-in-d   { from { opacity: 0; transform: translateY(14px) scale(.97); } to { opacity: 1; transform: none; } }
  @keyframes ms-modal-in-m   { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: none; } }
  @keyframes ms-slide-left   { from { opacity: 0; transform: translateX(18px); } to { opacity: 1; transform: none; } }
  @keyframes ms-slide-right  { from { opacity: 0; transform: translateX(-18px); } to { opacity: 1; transform: none; } }
  @keyframes ms-card-in      { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
  @keyframes ms-pulse        { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
  @keyframes ms-spin         { to { transform: rotate(360deg); } }

  @media (prefers-reduced-motion: reduce) {
    .ms-wrap *, .ms-portal-root * { animation-duration: .001ms !important; transition-duration: .001ms !important; }
  }

  /* ---------------------------------------------------------------- */
  /*  Trigger button (sidebar)                                         */
  /* ---------------------------------------------------------------- */

  .ms-wrap {
    width: 100%;
    display: flex;
    justify-content: center;
    position: relative;
    margin-bottom: 10px;
  }

  .sb-manage-opd-btn {
    width: 60%;
    max-width: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px 18px;
    min-height: 56px;
    border-radius: var(--ms-radius-md);
    text-decoration: none;
    font-size: 14.5px;
    font-weight: 700;
    letter-spacing: -0.15px;
    transition: transform .18s ease, background .18s ease, border-color .18s ease, box-shadow .18s ease;
    position: relative;
    cursor: pointer;
    border: 1.5px solid rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.04);
    color: #ffffff;
  }

  .sb-manage-opd-btn:hover:not(.active) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.95);
    transform: translateY(-1px);
  }

  .sb-manage-opd-btn:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  .sb-manage-opd-btn.active {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.95);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }

  .sb-manage-opd-btn .sb-nav-icon {
    background: transparent;
    color: #ffffff;
    display: flex;
    flex-shrink: 0;
  }

  .sb-manage-opd-btn .sb-nav-label {
    flex: none;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  /* ---------------------------------------------------------------- */
  /*  Overlay + modal shell                                             */
  /* ---------------------------------------------------------------- */

  .ms-overlay {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(11, 18, 32, 0.32);
    backdrop-filter: blur(6px) saturate(120%);
    -webkit-backdrop-filter: blur(6px) saturate(120%);
    z-index: 200000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: ms-overlay-in .2s ease;
  }

  body.has-active-modal > :not(.ms-portal-root),
  body.has-active-modal > .ms-portal-root > :not(.ms-overlay) {
    filter: blur(6px) !important;
    pointer-events: none !important;
    user-select: none !important;
    transition: filter 200ms ease !important;
  }

  .ms-modal {
    position: relative;
    width: min(680px, 100%);
    max-height: min(680px, 84vh);
    box-sizing: border-box;
    background: var(--ms-surface);
    border: 1px solid var(--ms-border);
    border-radius: 28px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: var(--ms-shadow-modal);
    animation: ms-modal-in-d .3s cubic-bezier(.16,1,.3,1);
    z-index: 200001;
  }

  /* ---------------------------------------------------------------- */
  /*  Header                                                            */
  /* ---------------------------------------------------------------- */

  .ms-modal-hd {
    position: relative;
    padding: 26px 28px 20px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    border-bottom: 1px solid var(--ms-border);
    background: linear-gradient(180deg, var(--ms-surface-alt) 0%, var(--ms-surface) 100%);
    overflow: hidden;
    flex-shrink: 0;
  }

  .ms-modal-hd::after {
    content: '';
    position: absolute;
    top: -36px;
    right: -30px;
    width: 150px;
    height: 150px;
    background: var(--ms-primary-tint);
    border-radius: 50%;
    pointer-events: none;
  }

  .ms-modal-hd-icon {
    width: 42px;
    height: 42px;
    border-radius: 13px;
    background: var(--ms-primary);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 6px 16px rgba(29, 78, 216, 0.28);
    position: relative;
  }

  .ms-modal-hd-text {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    position: relative;
    min-width: 0;
  }

  .ms-modal-title {
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.4px;
    color: var(--ms-ink);
    margin: 0;
    line-height: 1.25;
  }

  .ms-modal-sub {
    font-size: 13px;
    color: var(--ms-muted);
    margin-top: 4px;
    line-height: 1.5;
  }

  .ms-modal-close {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    border: 1px solid var(--ms-border);
    background: var(--ms-surface);
    color: var(--ms-muted);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 160ms ease;
    flex-shrink: 0;
    position: relative;
  }
  .ms-modal-close:hover {
    background: #fee2e2;
    border-color: #fecaca;
    color: #dc2626;
  }
  .ms-modal-close:focus-visible {
    outline: 2px solid var(--ms-primary);
    outline-offset: 2px;
  }

  /* mobile drag handle */
  .ms-drag-handle {
    display: none;
  }

  /* ---------------------------------------------------------------- */
  /*  Body                                                              */
  /* ---------------------------------------------------------------- */

  .ms-modal-bd {
    padding: 26px 28px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    background: var(--ms-surface);
  }

  .ms-modal-bd::-webkit-scrollbar { width: 8px; }
  .ms-modal-bd::-webkit-scrollbar-track { background: transparent; }
  .ms-modal-bd::-webkit-scrollbar-thumb { background: var(--ms-border); border-radius: 8px; }
  .ms-modal-bd::-webkit-scrollbar-thumb:hover { background: #c9d4e0; }

  /* ---------------------------------------------------------------- */
  /*  Action cards                                                      */
  /* ---------------------------------------------------------------- */

  .ms-actions-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    animation: ms-slide-left .22s ease;
  }

  .ms-action-card {
    position: relative;
    background: var(--ms-surface);
    border: 1.5px solid var(--ms-border);
    border-radius: var(--ms-radius-lg);
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    gap: 12px;
    cursor: pointer;
    transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease;
    box-shadow: var(--ms-shadow-card);
    animation: ms-card-in .3s ease backwards;
  }
  .ms-action-card:nth-child(1) { animation-delay: .02s; }
  .ms-action-card:nth-child(2) { animation-delay: .07s; }
  .ms-action-card:nth-child(3) { animation-delay: .12s; }

  .ms-action-card:hover,
  .ms-action-card:focus-visible {
    transform: translateY(-3px);
    box-shadow: var(--ms-shadow-card-hover);
  }
  .ms-action-card:focus-visible { outline: none; }

  .ms-action-card[data-variant="success"]:hover { border-color: var(--ms-success); }
  .ms-action-card[data-variant="primary"]:hover { border-color: var(--ms-primary); }
  .ms-action-card[data-variant="violet"]:hover  { border-color: var(--ms-violet); }

  .ms-action-icon {
    width: 46px;
    height: 46px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ms-action-card[data-variant="success"] .ms-action-icon { background: var(--ms-success-tint); color: var(--ms-success); }
  .ms-action-card[data-variant="primary"] .ms-action-icon { background: var(--ms-primary-tint); color: var(--ms-primary); }
  .ms-action-card[data-variant="violet"]  .ms-action-icon { background: var(--ms-violet-tint); color: var(--ms-violet); }

  .ms-action-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    padding: 3px 7px;
    border-radius: 999px;
    background: var(--ms-success-tint);
    color: var(--ms-success);
  }
  .ms-action-badge svg { animation: ms-pulse 1.6s ease-in-out infinite; }

  .ms-action-label {
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 15.5px;
    font-weight: 700;
    color: var(--ms-ink);
    margin: 0;
    letter-spacing: -0.2px;
  }

  .ms-action-desc {
    font-size: 12.5px;
    color: var(--ms-muted);
    line-height: 1.5;
    margin: 0;
  }

  .ms-action-arrow {
    margin-top: auto;
    padding-top: 4px;
    color: #c3ccd8;
    transition: transform 160ms ease, color 160ms ease;
    align-self: flex-end;
  }
  .ms-action-card:hover .ms-action-arrow { transform: translateX(3px); }
  .ms-action-card[data-variant="success"]:hover .ms-action-arrow { color: var(--ms-success); }
  .ms-action-card[data-variant="primary"]:hover .ms-action-arrow { color: var(--ms-primary); }
  .ms-action-card[data-variant="violet"]:hover  .ms-action-arrow { color: var(--ms-violet); }

  /* ---------------------------------------------------------------- */
  /*  Doctor selection screen                                           */
  /* ---------------------------------------------------------------- */

  .ms-doctors-screen { animation: ms-slide-right .22s ease; }

  .ms-back-row {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--ms-border);
  }

  .ms-back-btn {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    border: 1px solid var(--ms-border);
    background: var(--ms-surface);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--ms-muted);
    transition: all 160ms ease;
    flex-shrink: 0;
  }
  .ms-back-btn:hover { background: var(--ms-primary-tint); border-color: var(--ms-primary); color: var(--ms-primary); }
  .ms-back-btn:focus-visible { outline: 2px solid var(--ms-primary); outline-offset: 2px; }

  .ms-back-title {
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 14.5px;
    font-weight: 700;
    color: var(--ms-ink);
  }

  .ms-back-sub { font-size: 12.5px; color: var(--ms-muted); margin-top: 2px; }

  .ms-mode-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 700;
    margin-left: auto;
    flex-shrink: 0;
    white-space: nowrap;
  }
  .ms-mode-chip.open    { background: var(--ms-success-tint); color: var(--ms-success); }
  .ms-mode-chip.manage  { background: var(--ms-primary-tint); color: var(--ms-primary); }

  .ms-doctors-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
  }

  .ms-doctor-card {
    border-radius: var(--ms-radius-md);
    background: var(--ms-surface);
    border: 1.5px solid var(--ms-border);
    box-shadow: var(--ms-shadow-card);
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: transform .16s ease, border-color .16s ease, box-shadow .16s ease;
    text-align: left;
    animation: ms-card-in .28s ease backwards;
  }

  .ms-doctor-card:hover,
  .ms-doctor-card:focus-visible {
    transform: translateY(-2px);
    border-color: var(--ms-primary);
    box-shadow: var(--ms-shadow-card-hover);
  }
  .ms-doctor-card:focus-visible { outline: none; }

  .ms-doctor-avatar {
    width: 44px;
    height: 44px;
    border-radius: 13px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: #fff;
  }
  .ms-doctor-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: inherit; }

  .ms-doctor-info { min-width: 0; }

  .ms-doctor-name {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--ms-ink);
    word-break: break-word;
    line-height: 1.35;
    margin: 0;
  }

  .ms-doctor-spec {
    font-size: 12px;
    color: var(--ms-muted);
    margin: 2px 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ---------------------------------------------------------------- */
  /*  States (loading / empty)                                          */
  /* ---------------------------------------------------------------- */

  .ms-state {
    padding: 48px 20px;
    text-align: center;
    color: var(--ms-muted);
    font-size: 13.5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: var(--ms-surface-alt);
    border: 1.5px dashed var(--ms-border);
    border-radius: var(--ms-radius-lg);
  }

  .ms-state strong { color: var(--ms-ink); font-size: 14px; font-weight: 700; }

  .ms-spin { animation: ms-spin .9s linear infinite; color: var(--ms-primary); }

  /* ---------------------------------------------------------------- */
  /*  Footer                                                            */
  /* ---------------------------------------------------------------- */

  .ms-modal-ft {
    padding: 16px 28px 24px;
    flex-shrink: 0;
    border-top: 1px solid var(--ms-border);
    background: var(--ms-surface);
  }

  .ms-cancel-btn {
    width: 100%;
    padding: 13px 16px;
    border-radius: var(--ms-radius-sm);
    border: 1.5px solid var(--ms-border);
    background: var(--ms-surface);
    color: var(--ms-ink);
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    transition: all 160ms ease;
  }
  .ms-cancel-btn:hover { background: var(--ms-surface-alt); border-color: #c9d4e0; }
  .ms-cancel-btn:focus-visible { outline: 2px solid var(--ms-primary); outline-offset: 2px; }

  /* ---------------------------------------------------------------- */
  /*  Responsive                                                        */
  /* ---------------------------------------------------------------- */

  @media (max-width: 860px) {
    .ms-actions-grid { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 680px) {
    .ms-overlay { padding: 0; align-items: flex-end; }

    .ms-modal {
      width: 100%;
      max-width: 100%;
      max-height: 88vh;
      border-radius: 20px 20px 0 0;
      animation: ms-modal-in-m .28s cubic-bezier(.16,1,.3,1);
      padding-bottom: env(safe-area-inset-bottom, 0);
    }

    .ms-drag-handle {
      display: block;
      width: 40px;
      height: 4px;
      border-radius: 999px;
      background: var(--ms-border);
      margin: 10px auto 0;
      flex-shrink: 0;
    }

    .ms-modal-hd { padding: 14px 20px 16px; }
    .ms-modal-hd-icon { width: 38px; height: 38px; border-radius: 11px; }
    .ms-modal-title { font-size: 18px; }
    .ms-modal-bd { padding: 18px 20px; }
    .ms-modal-ft { padding: 12px 20px 18px; }

    .ms-actions-grid { grid-template-columns: 1fr; gap: 10px; }
    .ms-action-card { flex-direction: row; align-items: center; padding: 14px; gap: 14px; }
    .ms-action-arrow { align-self: center; margin-top: 0; margin-left: auto; }
    .ms-action-badge { position: static; margin-left: auto; order: 2; }

    .ms-doctors-grid { grid-template-columns: 1fr; }
    .ms-back-row { flex-wrap: wrap; }
    .ms-mode-chip { margin-left: 0; order: 3; }
  }

  @media (max-width: 380px) {
    .ms-modal-hd-text { gap: 10px; }
    .ms-modal-hd-icon { display: none; }
  }
`;

/* Category-appropriate color coding: emerald = live/active, indigo = configure, violet = broadcast */
const ACTIONS = [
  {
    id: "open",
    label: "Open OPD",
    desc: "View and manage the active patient queue",
    Icon: Stethoscope,
    variant: "success",
    badge: "Live",
  },
  {
    id: "manage",
    label: "Manage OPD",
    desc: "Configure sessions and slot settings",
    Icon: SlidersHorizontal,
    variant: "primary",
  },
  {
    id: "display",
    label: "Queue Display",
    desc: "Launch the live waiting room screen",
    Icon: Monitor,
    variant: "violet",
  },
];

/* Deterministic accent color per doctor, derived from their name */
const AVATAR_PALETTE = ["#1d4ed8", "#059669", "#7c3aed", "#d97706", "#dc2626", "#0891b2"];
function avatarColor(name = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}
function initials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

export default function ManageSessions({ isCollapsed, isExpired = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const clinicId = Number(auth?.clinic?.id) || null;
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorPhotos, setDoctorPhotos] = useState({});
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setMode(null);
    setDoctors([]);
  }, []);

  const back = () => setMode(null);

  // Prevent background scrolling when modal is open, and blur/disable background content
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prevOverflow = document.body.style.overflow;
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("has-active-modal");
    }
    return () => {
      document.body.classList.remove("has-active-modal");
      document.body.style.overflow = prevOverflow || "";
    };
  }, [isOpen]);

  // Close on Escape for keyboard accessibility
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const res = await fetch(buildApiUrl(`doctors/by-clinic-id/${clinicId}`), {
        headers: { "X-Clinic-Id": clinicId },
      });
      const data = await res.json();
      setDoctors(Array.isArray(data) ? data : []);
    } catch {
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clinicId || doctors.length === 0) {
      setDoctorPhotos({});
      return undefined;
    }

    let cancelled = false;
    const photoUrls = [];
    const headers = { "X-Clinic-Id": String(clinicId) };

    Promise.all(doctors.map(async (doctor) => {
      if (!doctor.id) return [doctor.id, ""];
      try {
        const response = await fetch(buildApiUrl(`doctors/${doctor.id}/photo/content`), {
          headers,
          credentials: "include",
        });
        if (!response.ok) return [doctor.id, ""];
        const photoUrl = URL.createObjectURL(await response.blob());
        photoUrls.push(photoUrl);
        return [doctor.id, photoUrl];
      } catch {
        return [doctor.id, ""];
      }
    })).then((entries) => {
      if (!cancelled) setDoctorPhotos(Object.fromEntries(entries));
      else photoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
    });

    return () => {
      cancelled = true;
      photoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
    };
  }, [clinicId, doctors]);

  const handleAction = (id) => {
    if (id === "display") {
      close();
      navigate("/admin-dashboard/queue-display");
      return;
    }
    setMode(id);
    if (!doctors.length) fetchDoctors();
  };

  const handleDoctor = (doctorId) => {
    close();
    navigate(
      mode === "open"
        ? `/admin-dashboard/doctor-opd/${doctorId}`
        : `/admin-dashboard/doctor-opd/${doctorId}/manage`
    );
  };

  const isActive = /^\/admin-dashboard\/doctor-opd\/(?!session\/)/.test(location.pathname);

  const modal = (
    <div className="ms-portal-root">
      <style>{STYLES}</style>
      <div
        className="ms-overlay"
        onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
        role="presentation"
      >
        <div
          className="ms-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ms-modal-title"
          ref={modalRef}
        >
          <div className="ms-drag-handle" aria-hidden="true" />

          {/* Header */}
          <div className="ms-modal-hd">
            <div className="ms-modal-hd-text">
              <div className="ms-modal-hd-icon" aria-hidden="true">
                <Stethoscope size={20} strokeWidth={2} />
              </div>
              <div>
                <h2 className="ms-modal-title" id="ms-modal-title">
                  {mode ? (mode === "open" ? "Open OPD" : "Manage OPD") : "Manage Sessions"}
                </h2>
                <div className="ms-modal-sub">
                  {mode ? "Select a doctor to continue" : "Choose an action to get started"}
                </div>
              </div>
            </div>
            <button className="ms-modal-close" onClick={close} aria-label="Close dialog">
              <X size={16} strokeWidth={2.25} />
            </button>
          </div>

          {/* Body */}
          <div className="ms-modal-bd">
            {!mode && (
              <div className="ms-actions-grid">
                {ACTIONS.map(({ id, label, desc, Icon, variant, badge }) => (
                  <button
                    key={id}
                    type="button"
                    className="ms-action-card"
                    data-variant={variant}
                    onClick={() => handleAction(id)}
                  >
                    {badge && (
                      <span className="ms-action-badge">
                        <Radio size={9} strokeWidth={3} />
                        {badge}
                      </span>
                    )}
                    <div className="ms-action-icon">
                      <Icon size={21} strokeWidth={1.75} />
                    </div>
                    <div>
                      <p className="ms-action-label">{label}</p>
                      <p className="ms-action-desc">{desc}</p>
                    </div>
                    <div className="ms-action-arrow" aria-hidden="true">
                      <ChevronRight size={16} strokeWidth={2.25} />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {mode && (
              <div className="ms-doctors-screen">
                <div className="ms-back-row">
                  <button className="ms-back-btn" onClick={back} aria-label="Back to actions">
                    <ChevronLeft size={16} strokeWidth={2.25} />
                  </button>
                  <div>
                    <div className="ms-back-title">Select doctor</div>
                    <div className="ms-back-sub">
                      {mode === "open" ? "Open OPD" : "Manage OPD"} session
                    </div>
                  </div>
                  <span className={`ms-mode-chip ${mode}`}>
                    {mode === "open" ? (
                      <><Stethoscope size={11} /> Open</>
                    ) : (
                      <><SlidersHorizontal size={11} /> Manage</>
                    )}
                  </span>
                </div>

                {loading && (
                  <div className="ms-state">
                    <Loader2 size={26} strokeWidth={1.75} className="ms-spin" />
                    <span>Loading doctors…</span>
                  </div>
                )}

                {!loading && doctors.length === 0 && (
                  <div className="ms-state">
                    <UserRound size={30} strokeWidth={1.25} style={{ opacity: 0.4 }} />
                    <strong>No doctors found</strong>
                    <span>This clinic doesn&rsquo;t have any doctors set up yet.</span>
                  </div>
                )}

                {!loading && doctors.length > 0 && (
                  <div className="ms-doctors-grid">
                    {doctors.map((doc, i) => (
                      <button
                        key={doc.id}
                        type="button"
                        className="ms-doctor-card"
                        style={{ animationDelay: `${Math.min(i * 0.03, 0.3)}s` }}
                        onClick={() => handleDoctor(doc.id)}
                      >
                        <div
                          className="ms-doctor-avatar"
                          style={{ background: avatarColor(doc.doctor_name) }}
                          aria-hidden="true"
                        >
                          {doctorPhotos[doc.id] && <img src={doctorPhotos[doc.id]} alt="" />}
                          {!doctorPhotos[doc.id] && initials(doc.doctor_name)}
                        </div>
                        <div className="ms-doctor-info">
                          <p className="ms-doctor-name">{doc.doctor_name}</p>
                          <p className="ms-doctor-spec">{doc.specialization || "Specialist"}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="ms-modal-ft">
            <button className="ms-cancel-btn" onClick={close}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ms-wrap" style={{ position: "relative", width: "100%" }}>
      <style>{STYLES}</style>

      <button
        type="button"
        className={`sb-nav-item sb-manage-opd-btn ${isOpen || isActive ? "active" : ""}${isExpired ? " disabled" : ""}`}
        onClick={() => !isExpired && setIsOpen(true)}
        disabled={isExpired}
        title={isExpired ? "Manage OPD is unavailable while your subscription is expired" : "Manage OPD"}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-disabled={isExpired}
      >
        <div className="sb-nav-icon">
          <Stethoscope size={19} />
        </div>
        <span className="sb-nav-label">Manage OPD</span>
      </button>

      {isOpen && typeof document !== "undefined" ? createPortal(modal, document.body) : null}
    </div>
  );
}